import useDebounce from "@/hooks/useDebounce";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { getDocumentationCount } from "@/pages/dashboard/utils/file";
import type { Tuple } from "@/interfaces/estimate-detail";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useDispatch, useSelector } from "@/redux/store";
import { useCallback, useEffect, useRef } from "react";
import type { FilterOptionType } from "../filter/FilterOption";
import { FilterTab } from "../filter/FilterTab";
import { Rooms } from "./Rooms";
import type { Category } from "./Rooms/Lines";
import { RoomsSkeleton } from "./skeleton/RoomsSkeleton";
import { RoomsSectionEmptyState } from "./Rooms/RoomsSectionEmptyState";
import { CreateRoomDialog } from "./Rooms/CreateRoomDialog";
import { useEstimateJobStatus } from "@/hooks/queries/jobs/useEstimateJobStatus";
import { useCatSyncPending } from "./cat3-sync/Cat3LineHighlightContext";

interface RoomsSectionProps {
  estimateId?: string;
  lineItemDetail: Tuple | undefined;
  deleteLineItem: (tupleId: string, pageId: string, uniqueId: string, category?: Category) => Promise<void>;
  onUpload?: () => void;
}

export const RoomsSection = ({ estimateId, deleteLineItem, onUpload }: RoomsSectionProps) => {
  const dispatch = useDispatch();
  const roomsRef = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const isLoading = useSelector(estimationSelector.isLoading);
  const currentRoom = useSelector(estimationSelector.currentRoom);
  const estimation = useSelector(estimationSelector.estimation);

  // "Processing finished" signal. `allCompleted` reflects the BullMQ job status (shares the query
  // cache with the page-level poll, so no extra polling); the persisted flag covers legacy-era
  // estimates that have no job records. A failed job also counts as finished — processing is over and
  // no further line items are coming, so holding the skeleton open would strand the user on it.
  const { allCompleted, hasFailedJobs } = useEstimateJobStatus(estimateId ?? "", { enabled: !!estimateId });
  const persistedAutoMergeCompleted = estimation?.autoMergeRoomCompleted ?? false;
  // Adding documentation to an already-settled estimate kicks off a fresh reprocess. While it runs
  // (scopeNoteProcessingStatus === "processing"), treat the estimate as not-yet-complete so an
  // empty room drops back to the loading skeleton instead of staying on the empty state. The poll
  // in useEstimateDetail refetches the line items once it flips to "complete".
  const isReprocessingDocumentation = estimation?.scopeNoteProcessingStatus === "processing";
  // A cat change offloads its line-item recompute to the worker; treat that window like reprocessing
  // so an empty room shows the loading skeleton rather than the empty state until the job finishes.
  const isCatSyncPending = useCatSyncPending();
  const processingComplete =
    !isReprocessingDocumentation && !isCatSyncPending && (allCompleted || hasFailedJobs || persistedAutoMergeCompleted);

  const {
    rooms,
    estimationDetail,
    selectLineItem,
    roomTabOptions,
    searchEstimateDetail,
    updateMeasurement,
    updateMetadataForExpectedDoc,
    updateLineItems,
  } = useEstimateDetail(estimateId!);

  const getRoomRef = useCallback(
    (roomName: string) => (el: HTMLDivElement | null) => {
      if (el) roomsRef.current.set(roomName.toLowerCase(), el);
    },
    [],
  );

  const debouncedScrollHandler = useDebounce(() => {
    const scrollContainer = document.querySelector(".rooms-section");
    if (!scrollContainer) return;

    let newCurrentRoomOption: FilterOptionType | undefined = undefined;

    for (const [roomId, element] of roomsRef.current.entries()) {
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= 250 && rect.bottom > 0) {
          const foundOption = roomTabOptions.find((option) => option.id.toLowerCase() === roomId.toLowerCase());
          if (foundOption) {
            newCurrentRoomOption = foundOption;
            break;
          }
        }
      }
    }

    if (newCurrentRoomOption && newCurrentRoomOption.id !== currentRoom?.id) {
      dispatch(
        estimationActions.setEstimationState({
          currentRoom: newCurrentRoomOption,
        }),
      );
    }
  }, 150);

  useEffect(() => {
    const scrollContainer = document.querySelector(".rooms-section");

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", debouncedScrollHandler);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", debouncedScrollHandler);
      }
    };
  }, [dispatch, roomTabOptions, currentRoom, roomsRef, debouncedScrollHandler]);

  const handleTabChange = (option: FilterOptionType) => {
    requestAnimationFrame(() => {
      const selectedRoom = roomsRef.current.get(option.id.toLowerCase());

      if (selectedRoom) {
        selectedRoom.scrollIntoView({ behavior: "smooth", block: "start" });

        dispatch(
          estimationActions.setEstimationState({
            currentRoom: option,
          }),
        );
      }
    });
  };

  const handleRoomCreated = useCallback(
    async (roomName: string) => {
      if (!estimateId) return;
      const updatedRooms = await searchEstimateDetail(estimateId, { silent: true });
      const newRoom = updatedRooms?.find((r) => r.dataForUI.roomName.toLowerCase() === roomName.toLowerCase());
      if (newRoom) {
        const newRoomOption: FilterOptionType = {
          id: newRoom.dataForUI.metadata.pageId,
          value: newRoom.dataForUI.roomName,
        };
        dispatch(estimationActions.setEstimationState({ currentRoom: newRoomOption }));

        // Wait for React to render the new room element before scrolling
        requestAnimationFrame(() => {
          const el = roomsRef.current.get(newRoom.dataForUI.metadata.pageId.toLowerCase());
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }
    },
    [estimateId, searchEstimateDetail, dispatch],
  );

  const hasItems = estimationDetail?.some((item) => item.dataForUI?.page.tuplesOutput?.length > 0) ?? false;
  const hasRooms = (estimationDetail?.length ?? 0) > 0;
  const hasContent = hasItems || hasRooms;

  // Whether the estimate has any documentation associated (files, docusketch links, or notes) —
  // the same count the dashboard shows. This is the source of truth for the empty state: an
  // estimate can't have content without documentation, so a zero count reliably means "nothing
  // uploaded yet". The estimation summary is seeded from the dashboard on navigation, so this is
  // known immediately without a metadata refetch.
  const estimationLoaded = estimation !== undefined;
  const hasDocumentation = estimation ? getDocumentationCount(estimation).totalFiles > 0 : false;

  const showRooms = hasContent && !isLoading;
  // Empty state once we know there's no documentation.
  const noDocumentationAndNoLineItems = estimationLoaded && !hasContent && !hasDocumentation;
  // Empty state when documentation exists but processing finished without producing any line items
  // (e.g. a scope note that generated nothing, or every line item/room archived). Without this the
  // skeleton would spin forever since there is documentation but never any content.
  const processedWithoutLineItems = estimationLoaded && !isLoading && !hasContent && hasDocumentation && processingComplete;
  const showEmptyState = !showRooms && (noDocumentationAndNoLineItems || processedWithoutLineItems);
  // Otherwise skeleton: estimation still loading, or documentation exists but line items
  // haven't been generated yet (processing).
  const showSkeleton = !showRooms && !showEmptyState;

  return (
    <div className="flex h-full w-full border-collapse flex-col border-r border-[#eeeff1]">
      <div className="flex flex-row items-center justify-between py-1">
        <div className="grid grid-cols-1">
          <FilterTab
            scrollButton
            className="mx-2"
            selectedOption={currentRoom}
            defaultOption={roomTabOptions[0]}
            options={roomTabOptions}
            onSelectOption={handleTabChange}
          />
        </div>
        {estimateId && (
          <CreateRoomDialog
            estimateId={estimateId}
            existingRoomNames={roomTabOptions.map((o) => o.value)}
            disabled={showSkeleton}
            onCreated={handleRoomCreated}
          />
        )}
      </div>
      <div className="rooms-section flex flex-1 flex-col gap-6 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {showSkeleton && <RoomsSkeleton />}
        {showEmptyState &&
          (processedWithoutLineItems ? (
            <RoomsSectionEmptyState
              onUpload={onUpload}
              title="No line items generated"
              description="Processing finished but didn't produce any line items. Add more documentation to try again."
            />
          ) : (
            <RoomsSectionEmptyState onUpload={onUpload} />
          ))}
        {showRooms && (
          <Rooms
            estimateId={estimateId}
            rooms={rooms}
            onItemLineDelete={deleteLineItem}
            onSelectItemLine={selectLineItem}
            updateMeasurement={updateMeasurement}
            updateMetadataForExpectedDoc={updateMetadataForExpectedDoc}
            updateLineItems={updateLineItems}
            getRoomRef={getRoomRef}
          />
        )}
      </div>
    </div>
  );
};
